import React, { FC, useEffect, useState, MutableRefObject } from 'react';
import { Box, Button, InputLabel, FormControlLabel, FormControl, Select, MenuItem, Checkbox } from '@mui/material';
import { OrgActionSet } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    getActionSetTemplate,
    setActionSetActiveTemplate,
    updateActionSetCurrencyTemplate,
} from 'apollo/templates/action-sets';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ActionSet } from 'models/action-set';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';
import { Currency } from 'models/country';

type ActionSetProps = {
    orgActionSets: OrgActionSet[] | undefined;
    selectedActionSetId: string;
    setSelectedActionSetId: (id: string) => void;
    activeActionSet: string;
    setActiveActionSet: (id: string) => void;
    onActionSetChange: (actionSet: ActionSet, loading: boolean) => void;
    onAddActionSet: () => void;
    reFetchActionSetRef?: MutableRefObject<((actionSetId: string) => Promise<any>) | null>;
};

const ActionSets: FC<ActionSetProps> = ({
    orgActionSets,
    selectedActionSetId,
    setSelectedActionSetId,
    activeActionSet,
    setActiveActionSet,
    onActionSetChange,
    onAddActionSet,
    reFetchActionSetRef,
}: ActionSetProps) => {
    const { t: translate } = useTranslation(['manage-actions']);
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [selectedActionSetCurrency, setSelectedActionSetCurrency] = useState('');

    useEffect(() => {
        const selectedActionSet = orgActionSets?.find((actionSet) => actionSet._id === selectedActionSetId);
        setSelectedActionSetCurrency(selectedActionSet?.currency || '');
    }, [selectedActionSetId, orgActionSets]);

    const [getActionSet, { loading, refetch }] = useLazyQuery(getActionSetTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ actionSet }) => {
            onActionSetChange(actionSet, loading);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [setActionSetActive] = useMutation(setActionSetActiveTemplate, {
        onCompleted: ({ setActionSetActive: actionSet }) => {
            setActiveActionSet(actionSet._id);
            currentOrgVar({ ...currentOrg, activeActionSet: actionSet._id });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateActionSetCurrency] = useMutation(updateActionSetCurrencyTemplate, {
        onCompleted: ({ updateActionSetCurrency: actionSet }) => {
            setSelectedActionSetCurrency(actionSet.currency);
            const organization = currentOrgVar();
            currentOrgVar({
                ...organization,
                actionSets: organization.actionSets?.map((orgActionSet) =>
                    orgActionSet._id === actionSet._id
                        ? { ...orgActionSet, currency: actionSet.currency }
                        : orgActionSet,
                ),
            });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (reFetchActionSetRef && currentOrg._id && selectedActionSetId) {
            reFetchActionSetRef.current = (actionSetId: string) => {
                const idToFetch = actionSetId || selectedActionSetId;
                if (!idToFetch || !currentOrg._id) return Promise.resolve();

                return refetch({
                    actionSetId: idToFetch,
                    orgId: currentOrg._id,
                }).then((res) => {
                    const actionSet = res?.data?.actionSet;
                    if (actionSet) {
                        onActionSetChange(actionSet, false);
                    }
                    return res;
                });
            };
        }
    }, [refetch, reFetchActionSetRef, currentOrg._id, selectedActionSetId]);

    const handleActionSetChange = (actionSetId: string) => {
        const currentOrg = currentOrgVar();
        setSelectedActionSetId(actionSetId);
        if (actionSetId === currentOrg.activeActionSet) {
            setActiveActionSet(currentOrg.activeActionSet);
        } else {
            setActiveActionSet('');
        }
        getActionSet({
            variables: {
                actionSetId: actionSetId,
                orgId: currentOrg._id,
            },
        });
    };

    const handleActiveActionSetChange = (isChecked: boolean) => {
        const value = isChecked ? selectedActionSetId : '';
        setActionSetActive({
            variables: {
                actionSetId: value,
            },
        });
    };

    return (
        <Box className="categories-section section-header">
            <Box className="manage-action-filter" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="action-set-label">{translate('manage-actions:ACTION_SET')}</InputLabel>
                    <Select
                        labelId="action-set-label"
                        value={selectedActionSetId}
                        label={translate('manage-actions:ACTION_SET')}
                        onChange={(e) => handleActionSetChange(e.target.value)}
                    >
                        {orgActionSets?.map((actionSet) => (
                            <MenuItem key={actionSet._id} value={actionSet._id}>
                                {actionSet.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!activeActionSet}
                            onChange={(e) => handleActiveActionSetChange(e.target.checked)}
                            name="activeActionSet"
                            id="activeActionSet"
                            disabled={!!activeActionSet}
                        />
                    }
                    label={translate('manage-actions:ACTIVE_ACTION_SET')}
                    sx={{ lineHeight: 0 }}
                />
                {selectedActionSetId ? (
                    <Box sx={{ minWidth: 160 }}>
                        <CurrencyInput
                            currency={{ code: selectedActionSetCurrency } as Currency}
                            onCurrencySelect={(selectedCurrency) =>
                                updateActionSetCurrency({
                                    variables: {
                                        actionSetId: selectedActionSetId,
                                        currency: selectedCurrency.code,
                                    },
                                })
                            }
                        />
                    </Box>
                ) : undefined}
                <Box sx={{ marginLeft: 'auto' }}>
                    <Button variant="contained" onClick={onAddActionSet}>
                        + {translate('manage-actions:ADD_ACTION_SET')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
export default ActionSets;
